import { useEffect, useState } from "react";
import { getApp } from "firebase/app";
import { doc, getFirestore, onSnapshot } from "firebase/firestore";
import { Button } from "@firecms/ui";
import { httpsCallable } from "firebase/functions";

import {
  eventTaskPath,
  parseEventTask,
  type EventTask,
  type EventTaskId,
} from "../collections/eventTask";
import { useFirebaseFunctions } from "../hooks/useFirebaseFunctions";
import type { AdminTranslateFn } from "../i18n/getAdminT";
import type { Locale } from "../localization/constants";
import type { EventCollectionId } from "../workspace/collectionIds";
import { statsMuted } from "./statsViewTheme";

type RankingRefreshResult = {
  ok: boolean;
  queued: boolean;
};

type RankingRefreshBarProps = {
  eventId: EventCollectionId;
  ranksUpdatedAt: number;
  locale: Locale;
  t: AdminTranslateFn;
  /** Defaults to individual ranking refresh. */
  callableName?: "refreshEventLeaderboard" | "refreshEventTeamLeaderboard";
};

function cooldownRemainingMs(
  compute: EventTask | null,
  nowMs: number
): number {
  if (!compute?.cooldownUntil || compute.status === "running") {
    return 0;
  }
  const remaining = compute.cooldownUntil.getTime() - nowMs;
  return remaining > 0 ? remaining : 0;
}

export function RankingRefreshBar({
  eventId,
  ranksUpdatedAt,
  locale,
  t,
  callableName = "refreshEventLeaderboard",
}: RankingRefreshBarProps) {
  const functions = useFirebaseFunctions();
  const [queueing, setQueueing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [compute, setCompute] = useState<EventTask | null>(null);
  const [nowMs, setNowMs] = useState(() => Date.now());

  const taskId: EventTaskId =
    callableName === "refreshEventTeamLeaderboard"
      ? "teamRanking"
      : "individualRanking";

  useEffect(() => {
    setError(null);
    setCompute(null);
    const firestore = getFirestore(getApp());
    return onSnapshot(
      doc(firestore, eventTaskPath(eventId.accountId, eventId.eventId, taskId)),
      (snapshot) => {
        setCompute(parseEventTask(snapshot.data()));
      }
    );
  }, [eventId.accountId, eventId.eventId, taskId]);

  const running = queueing || compute?.status === "running";
  const remainingMs = cooldownRemainingMs(compute, nowMs);

  useEffect(() => {
    if (!running && remainingMs <= 0) {
      return;
    }
    const interval = window.setInterval(() => setNowMs(Date.now()), 1000);
    return () => window.clearInterval(interval);
  }, [running, remainingMs]);

  const updatedAt = compute?.completedAt ?? (ranksUpdatedAt > 0 ? new Date(ranksUpdatedAt) : null);
  const lastUpdated = updatedAt
    ? t("eventRanking.lastUpdated", {
        when: updatedAt.toLocaleString(locale),
      })
    : t("eventRanking.lastUpdatedNever");

  const status = running
    ? `${t("eventRanking.refreshing")} · ${compute?.progress ?? 0}%`
    : compute?.status === "failed" && compute.error
      ? compute.error
      : remainingMs > 0
        ? t("eventRanking.refreshCooldown", {
            seconds: Math.max(1, Math.ceil(remainingMs / 1000)),
          })
        : null;

  const queueRefresh = async () => {
    if (!functions || running || remainingMs > 0) {
      return;
    }
    setQueueing(true);
    setError(null);
    try {
      const callable = httpsCallable<
        { accountId: string; eventId: string },
        RankingRefreshResult
      >(functions, callableName);
      await callable({
        accountId: eventId.accountId,
        eventId: eventId.eventId,
      });
    } catch (caught) {
      const message =
        caught instanceof Error ? caught.message : t("eventRanking.refreshError");
      setError(message || t("eventRanking.refreshError"));
    } finally {
      setQueueing(false);
    }
  };

  return (
    <div className="flex flex-wrap items-center justify-between gap-3">
      <p className={`text-sm ${statsMuted}`}>
        {lastUpdated}
        {status ? ` · ${status}` : ""}
        {error ? ` · ${error}` : ""}
      </p>
      <Button
        variant="outlined"
        disabled={running || remainingMs > 0 || !functions}
        onClick={() => void queueRefresh()}
      >
        {running
          ? `${t("eventRanking.refreshing")} ${compute?.progress ?? 0}%`
          : t("eventRanking.refresh")}
      </Button>
    </div>
  );
}
